import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Card, CardBody, CardHeader, Spinner, Nav, NavItem, NavLink,
} from 'reactstrap';
import { withTranslation } from 'react-i18next';

import { get } from '../../helpers/api_helper';

const summaryChartColors = ['#3b82f6', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

function CategoryBreakdownCard(props) {
  const { t, title, params } = props;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [activeCategoryTreeTab, setActiveCategoryTreeTab] = useState('');

  const queryString = new URLSearchParams(
    Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        acc[key] = value;
      }

      return acc;
    }, {}),
  ).toString();

  useEffect(() => {
    const loadCategoryBreakdown = async () => {
      if (queryString === '') {
        setCategoryBreakdown([]);
        setActiveCategoryTreeTab('');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await get(`/api/listings/category-breakdown?${queryString}`);
        setCategoryBreakdown(Array.isArray(response) ? response : []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    loadCategoryBreakdown();
  }, [queryString]);

  useEffect(() => {
    if (categoryBreakdown.length === 0) {
      setActiveCategoryTreeTab('');
      return;
    }

    const hasActiveTree = categoryBreakdown.some(
      (tree) => String(tree.tree_id) === String(activeCategoryTreeTab),
    );

    if (!hasActiveTree) {
      setActiveCategoryTreeTab(String(categoryBreakdown[0].tree_id));
    }
  }, [categoryBreakdown, activeCategoryTreeTab]);

  const getCategoryRoot = (node) => {
    if (!node?.path_segments || node.path_segments.length === 0) {
      return node?.category_name || '';
    }

    return node.path_segments[0];
  };

  const renderCategoryHierarchy = (
    nodes = [],
    colorMap = {},
    parentQty = 0,
    depth = 0,
  ) => nodes.map((node) => (
    <div key={node.node_id || node.path}>
      <div
        className="d-flex align-items-start justify-content-between gap-3 py-2 border-bottom sourcing-order-page__category-key-row"
        style={{ paddingLeft: `${depth * 1.25}rem` }}
      >
        <div className="d-flex align-items-start gap-2 flex-grow-1">
          <span
            className="rounded-circle flex-shrink-0 mt-1"
            style={{
              width: '10px',
              height: '10px',
              backgroundColor:
                colorMap[getCategoryRoot(node)]
                || summaryChartColors[depth % summaryChartColors.length],
              opacity: Math.max(1 - (depth * 0.12), 0.5),
            }}
          />
          <div>
            <div className={depth === 0 ? 'fw-semibold' : ''}>
              {node.category_name}
            </div>
            {depth > 0 && (
              <div className="text-muted small sourcing-order-page__category-key-path">
                {node.category_full_name}
              </div>
            )}
            <div className="sourcing-order-page__category-bar mt-2">
              <div
                className="sourcing-order-page__category-bar-fill"
                style={{
                  width: `${parentQty > 0 ? Math.max((node.qty / parentQty) * 100, 4) : 0}%`,
                  backgroundColor:
                    colorMap[getCategoryRoot(node)]
                    || summaryChartColors[depth % summaryChartColors.length],
                  opacity: Math.max(1 - (depth * 0.12), 0.5),
                }}
              />
            </div>
          </div>
        </div>
        <span className="fw-semibold">{node.qty}</span>
      </div>
      {node.children && node.children.length > 0
        ? renderCategoryHierarchy(node.children, colorMap, node.qty, depth + 1)
        : null}
    </div>
  ));

  const activeTree = categoryBreakdown.find(
    (tree) => String(tree.tree_id) === String(activeCategoryTreeTab),
  ) || categoryBreakdown[0];

  const rootNodes = activeTree?.hierarchy || [];
  const totalQty = rootNodes.reduce((sum, node) => sum + node.qty, 0);
  const colorMap = rootNodes.reduce((acc, node, index) => ({
    ...acc,
    [getCategoryRoot(node)]: summaryChartColors[index % summaryChartColors.length],
  }), {});

  return (
    <Card className="sourcing-supplier-page__panel-card sourcing-supplier-page__tab-card">
      <CardHeader className="bg-transparent border-bottom sourcing-supplier-page__panel-header">
        <div className="sourcing-supplier-page__panel-title">{title}</div>
      </CardHeader>
      <CardBody className="sourcing-supplier-page__panel-body">
        {loading && (
          <div className="d-flex align-items-center justify-content-center py-4">
            <Spinner size="sm" color="primary" />
          </div>
        )}

        {!loading && error !== '' && (
          <div className="text-danger py-2">
            {error}
          </div>
        )}

        {!loading && error === '' && categoryBreakdown.length > 0 && (
          <>
            <Nav tabs className="sourcing-order-page__subtabs mb-3">
              {categoryBreakdown.map((tree) => (
                <NavItem key={tree.tree_id}>
                  <NavLink
                    className={classnames(
                      'sourcing-order-page__subtab',
                      { active: String(tree.tree_id) === String(activeCategoryTreeTab) },
                    )}
                    onClick={() => setActiveCategoryTreeTab(String(tree.tree_id))}
                  >
                    {tree.tree_name}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            {activeTree ? (
              <div>
                <div className="text-muted small text-uppercase mb-3">
                  {activeTree.tree_title || activeTree.tree_name}
                </div>
                <div
                  className="pe-2"
                  style={{ maxHeight: '320px', overflowY: 'auto' }}
                >
                  {rootNodes.length > 0 ? (
                    renderCategoryHierarchy(rootNodes, colorMap, totalQty)
                  ) : (
                    <div className="text-muted">
                      {t('No category data')}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-muted">
                {t('No category data')}
              </div>
            )}
          </>
        )}

        {!loading && error === '' && categoryBreakdown.length === 0 && (
          <div className="text-muted">
            {t('No category data')}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

CategoryBreakdownCard.propTypes = {
  params: PropTypes.object,
  t: PropTypes.func.isRequired,
  title: PropTypes.string,
};

CategoryBreakdownCard.defaultProps = {
  params: {},
  title: 'Category Trees',
};

export default withTranslation()(CategoryBreakdownCard);
